import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { formatDateOnlyLong, getLocalDateKey, getVisibleWeekDays } from '../utils/agendaDates';
import VisitCustomerFields from './VisitCustomerFields';

const emptyForm = {
    plannedDate: '',
    plannedStartTime: '09:00',
    plannedEndTime: '10:00',
    visitType: 'existingClient',
    externalClientId: '',
    externalClientBusinessUnit: '',
    clientName: '',
    crmCompany: null,
    crmOpportunity: null,
    prospectName: '',
    areaName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    objective: '',
    plannedTopics: '',
    preparationNotes: ''
};

const toForm = (visit) => ({
    ...emptyForm,
    plannedDate: visit?.plannedDate || '',
    plannedStartTime: visit?.plannedStartTime || '09:00',
    plannedEndTime: visit?.plannedEndTime || '10:00',
    visitType: visit?.visitType || 'existingClient',
    externalClientId: visit?.externalClientId || '',
    externalClientBusinessUnit: visit?.externalClientBusinessUnit || '',
    clientName: visit?.clientName || '',
    crmCompany: visit?.crmCompany || null,
    crmOpportunity: visit?.crmOpportunity || null,
    prospectName: visit?.prospectName || '',
    areaName: visit?.areaName || '',
    contactName: visit?.contactName || '',
    contactPhone: visit?.contactPhone || '',
    contactEmail: visit?.contactEmail || '',
    address: visit?.address || '',
    objective: visit?.objective || '',
    plannedTopics: visit?.plannedTopics || '',
    preparationNotes: visit?.preparationNotes || ''
});

const cleanPayload = (form) => {
    const payload = {
        plannedDate: form.plannedDate,
        plannedStartTime: form.plannedStartTime,
        plannedEndTime: form.plannedEndTime,
        visitType: form.visitType,
        contactName: form.contactName.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        contactEmail: form.contactEmail.trim() || undefined,
        address: form.address.trim() || undefined,
        objective: form.objective.trim(),
        plannedTopics: form.plannedTopics.trim() || undefined,
        preparationNotes: form.preparationNotes.trim() || undefined
    };

    if (form.visitType === 'existingClient') {
        payload.externalClientId = form.externalClientId;
        payload.externalClientBusinessUnit = form.externalClientBusinessUnit;
    } else if (form.visitType === 'prospect') {
        payload.prospectName = form.prospectName.trim();
        payload.crmCompany = form.crmCompany || null;
        payload.crmOpportunity = form.crmOpportunity || null;
    } else {
        payload.areaName = form.areaName.trim();
    }

    return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined && value !== ''));
};

const VisitDialog = ({
    visible,
    selectedDate,
    weekDays,
    visit,
    customers,
    loadingCustomers,
    customerLoadError,
    onReloadCustomers,
    loading,
    saving,
    onHide,
    onSubmit,
    onSave
}) => {
    const isSaving = Boolean(loading || saving);
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});

    const dayOptions = useMemo(
        () =>
            (weekDays?.length ? weekDays : getVisibleWeekDays(selectedDate)).map((day) => ({
                label: `${day.label}, ${formatDateOnlyLong(getLocalDateKey(day.date))}`,
                value: getLocalDateKey(day.date)
            })),
        [selectedDate, weekDays]
    );

    useEffect(() => {
        if (!visible) return;
        const selectedDateKey = getLocalDateKey(selectedDate);
        const defaultPlannedDate = dayOptions.some((option) => option.value === selectedDateKey)
            ? selectedDateKey
            : dayOptions[0]?.value || selectedDateKey;
        setForm(visit ? toForm(visit) : { ...emptyForm, plannedDate: defaultPlannedDate });
        setErrors({});
    }, [dayOptions, selectedDate, visible, visit]);

    const updateField = (field, value) => {
        setForm((current) => {
            const next = { ...current, [field]: value };
            if (field === 'visitType') {
                next.externalClientId = '';
                next.externalClientBusinessUnit = '';
                next.clientName = '';
                next.prospectName = '';
                next.crmCompany = null;
                next.crmOpportunity = null;
                next.areaName = '';
                next.contactName = '';
                next.contactPhone = '';
                next.contactEmail = '';
                next.address = '';
            }
            return next;
        });
        setErrors((current) => ({ ...current, [field]: null }));
    };

    const updateCustomer = (customer) => {
        setForm((current) => ({
            ...current,
            externalClientId: customer?.quickBooksId || '',
            externalClientBusinessUnit: customer?.businessUnit || '',
            clientName: customer?.displayName || '',
            contactName: customer?.contactName || '',
            contactPhone: customer?.contactPhone || '',
            contactEmail: customer?.contactEmail || '',
            address: customer?.address || ''
        }));
        setErrors((current) => ({
            ...current,
            externalClientId: null,
            externalClientBusinessUnit: null
        }));
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.plannedDate) nextErrors.plannedDate = 'Selecciona el día de la visita.';
        if (!form.plannedStartTime) nextErrors.plannedStartTime = 'Indica la hora de inicio.';
        if (!form.plannedEndTime) nextErrors.plannedEndTime = 'Indica la hora de finalización.';
        if (form.plannedStartTime && form.plannedEndTime && form.plannedEndTime <= form.plannedStartTime) {
            nextErrors.plannedEndTime = 'La hora final debe ser posterior a la hora inicial.';
        }
        if (!form.visitType) nextErrors.visitType = 'Selecciona el tipo de visita.';
        if (
            form.visitType === 'existingClient' &&
            (!form.externalClientId || !form.externalClientBusinessUnit)
        ) {
            nextErrors.externalClientId = 'Selecciona un cliente de QuickBooks.';
        }
        if (form.visitType === 'prospect' && !form.prospectName.trim()) nextErrors.prospectName = 'El nombre del prospecto o empresa es obligatorio.';
        if (form.visitType === 'areaProspecting' && !form.areaName.trim()) nextErrors.areaName = 'Indica la zona, ruta o sector del recorrido.';
        if (!form.objective.trim()) nextErrors.objective = 'El objetivo de la visita es obligatorio.';
        if (form.objective.trim().length > 1000) nextErrors.objective = 'Máximo 1000 caracteres.';
        if (form.plannedTopics.trim().length > 2000) nextErrors.plannedTopics = 'Máximo 2000 caracteres.';
        if (form.preparationNotes.trim().length > 2000) nextErrors.preparationNotes = 'Máximo 2000 caracteres.';
        if (form.address.trim().length > 500) nextErrors.address = 'Máximo 500 caracteres.';
        if (form.contactName.trim().length > 180) nextErrors.contactName = 'Máximo 180 caracteres.';
        if (form.contactPhone.trim().length > 80) nextErrors.contactPhone = 'Máximo 80 caracteres.';
        if (
            form.contactEmail.trim() &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())
        ) {
            nextErrors.contactEmail = 'Ingresa un correo electrónico válido.';
        }
        if (form.prospectName.trim().length > 180) nextErrors.prospectName = 'Máximo 180 caracteres.';
        if (form.areaName.trim().length > 240) nextErrors.areaName = 'Máximo 240 caracteres.';

        setErrors(nextErrors);
        return !Object.keys(nextErrors).length;
    };

    const handleSave = () => {
        if (!validate()) return;
        const submit = onSubmit || onSave;
        if (submit) submit(cleanPayload(form));
    };

    const footer = (
        <div className="flex flex-column sm:flex-row justify-content-end gap-2">
            <Button type="button" label="Cancelar" className="p-button-text" onClick={onHide} disabled={isSaving} />
            <Button type="button" label={visit ? 'Guardar cambios' : 'Guardar visita'} icon="pi pi-check" onClick={handleSave} loading={isSaving} disabled={isSaving} />
        </div>
    );

    const error = (field) => (errors[field] ? <small className="p-error block mt-1">{errors[field]}</small> : null);

    return (
        <Dialog className="ventas-dialog ventas-visit-dialog" header={visit ? 'Editar visita' : 'Nueva visita'} visible={visible} onHide={onHide} modal draggable={false} footer={footer} style={{ width: '42rem' }} breakpoints={{ '960px': '75vw', '640px': '96vw' }}>
            <div className="grid p-fluid ventas-visit-form">
                <div className="col-12">
                    <label className="block font-medium mb-2">Día de la visita</label>
                    <Dropdown value={form.plannedDate} options={dayOptions} onChange={(event) => updateField('plannedDate', event.value)} placeholder="Selecciona un día" />
                    {error('plannedDate')}
                </div>
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Hora de inicio</label>
                    <InputText type="time" value={form.plannedStartTime} onChange={(event) => updateField('plannedStartTime', event.target.value)} />
                    {error('plannedStartTime')}
                </div>
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Hora de finalización</label>
                    <InputText type="time" value={form.plannedEndTime} onChange={(event) => updateField('plannedEndTime', event.target.value)} />
                    {error('plannedEndTime')}
                    {form.plannedStartTime && form.plannedEndTime && form.plannedEndTime > form.plannedStartTime ? (
                        <small className="text-color-secondary block mt-1">
                            Duración estimada: {Math.round((Number(form.plannedEndTime.slice(0, 2)) * 60 + Number(form.plannedEndTime.slice(3))) - (Number(form.plannedStartTime.slice(0, 2)) * 60 + Number(form.plannedStartTime.slice(3))))} minutos
                        </small>
                    ) : null}
                </div>
                <VisitCustomerFields
                    form={form}
                    customers={customers}
                    loadingCustomers={loadingCustomers}
                    customerLoadError={customerLoadError}
                    onReloadCustomers={onReloadCustomers}
                    onFieldChange={updateField}
                    onCustomerChange={updateCustomer}
                    error={error}
                />
                <div className="col-12">
                    <label className="block font-medium mb-2">{form.visitType === 'areaProspecting' ? 'Objetivo del recorrido' : 'Objetivo de la visita'}</label>
                    <InputTextarea value={form.objective} rows={3} maxLength={1000} autoResize onChange={(event) => updateField('objective', event.target.value)} placeholder={form.visitType === 'areaProspecting' ? 'Identificar comercios con potencial y registrar oportunidades para visitas de seguimiento.' : 'Presentar el nuevo catálogo y revisar necesidades del próximo mes.'} />
                    {error('objective')}
                </div>
                <div className="col-12">
                    <label className="block font-medium mb-2">Temas previstos</label>
                    <InputTextarea value={form.plannedTopics} rows={3} maxLength={2000} autoResize onChange={(event) => updateField('plannedTopics', event.target.value)} />
                    {error('plannedTopics')}
                </div>
                <div className="col-12">
                    <label className="block font-medium mb-2">Notas de preparación</label>
                    <InputTextarea value={form.preparationNotes} rows={3} maxLength={2000} autoResize onChange={(event) => updateField('preparationNotes', event.target.value)} />
                    {error('preparationNotes')}
                </div>
            </div>
        </Dialog>
    );
};

export default VisitDialog;
